/**
 * Encrypted history export and import (ADR-016 stage 6).
 *
 * This is not a convenience. With the Double Ratchet, message keys are
 * destroyed after use and a new device starts with an empty chat — so this
 * file is the only way a conversation survives changing phones. ADR-016 makes
 * it a precondition for turning encryption on, not a feature to add later.
 *
 * The archive carries attachment BYTES, not just their keys. Ciphertext on the
 * server has a limited life; an export holding only keys would restore a
 * conversation with every picture missing, and would do it quietly.
 *
 * Layout:
 *
 *   "VOXARCH1"      8   magic, so a wrong file is refused before the passphrase
 *   version         1
 *   kdf             1   1 = PBKDF2-SHA512
 *   iterations      4   recorded, so an old archive still opens after we raise it
 *   salt           16
 *   chunk*              u32 length || nonce(24) || secretbox(<=256 KiB)
 *
 * Chunked because an archive is as big as somebody's photo history, and one
 * buffer of that size is how a phone runs out of memory during the operation
 * they were told would save their data. Each chunk carries its own random
 * nonce: a counter over a key derived once is exactly the reuse risk the
 * ratchet was built to avoid.
 */
import nacl from 'tweetnacl';

const MAGIC = new Uint8Array([0x56, 0x4f, 0x58, 0x41, 0x52, 0x43, 0x48, 0x31]);   // VOXARCH1
export const ARCHIVE_VERSION = 1;
export const KDF_PBKDF2_SHA512 = 1;
export const CHUNK_BYTES = 256 * 1024;
export const HEADER_BYTES = 8 + 1 + 1 + 4 + 16;

/**
 * OWASP's 210 000 is quoted for PBKDF2-HMAC-SHA256; SHA-512 does more work per
 * iteration, so the same count is stronger, not weaker. It is written into the
 * archive so raising it later cannot orphan a file somebody already made.
 *
 * Measured, not guessed. On an M-series Mac under Node's WebCrypto:
 * 50k = 14 ms, 100k = 24 ms, 210k = 51 ms, 400k = 97 ms — linear, as it should
 * be, and cheap because this is native code rather than JS.
 *
 * That is the fast end. ADR-011 §11 still requires the number measured on an
 * old iPhone inside WKWebView before it is fixed: 10-20 s there is a real
 * possibility, and a spinner nobody warned about reads as a hang. The count
 * travels in the archive, so raising it later cannot orphan a file somebody
 * already made — which is what makes it safe to ship this default and revise
 * it once the phone has answered (astrocall: измерить PBKDF2 на старом iPhone).
 */
export const DEFAULT_ITERATIONS = 210000;

function concat(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}
function u32be(n) {
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}
function readU32be(b, off) {
  return ((b[off] << 24) | (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3]) >>> 0;
}
function equal(a, b) {
  if (a.length !== b.length) return false;
  let d = 0; for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

function subtle() {
  const c = globalThis.crypto;
  if (!c?.subtle) {
    // Asserted at app start (ADR-011 §9): under a custom WebKit scheme
    // crypto.subtle is undefined, and this is the feature that disappears.
    throw new Error('crypto.subtle is unavailable — history export cannot work here');
  }
  return c.subtle;
}

export async function deriveArchiveKey(passphrase, salt, iterations = DEFAULT_ITERATIONS) {
  const s = subtle();
  const material = await s.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveBits']);
  const bits = await s.deriveBits({ name: 'PBKDF2', salt, iterations, hash: 'SHA-512' }, material, 256);
  return new Uint8Array(bits);
}

export function parseArchiveHeader(bytes) {
  if (!bytes || bytes.length < HEADER_BYTES) return null;
  if (!equal(bytes.slice(0, 8), MAGIC)) return null;
  const version = bytes[8];
  if (version !== ARCHIVE_VERSION) return null;
  if (bytes[9] !== KDF_PBKDF2_SHA512) return null;
  return {
    version,
    iterations: readU32be(bytes, 10),
    salt: bytes.slice(14, 30),
    body: bytes.slice(HEADER_BYTES),
  };
}

/**
 * @param onProgress called with (done, total) so the UI can show a real bar.
 *   The export is two passes over the data — serialise, then encrypt — and a
 *   bar that pretends otherwise is the lie ADR-011 §11 warned about.
 */
export async function packArchive(passphrase, payload, { iterations = DEFAULT_ITERATIONS, onProgress } = {}) {
  if (typeof passphrase !== 'string' || passphrase.length < 8) {
    throw new Error('Парольная фраза должна быть не короче 8 символов');
  }
  const salt = nacl.randomBytes(16);
  const key = await deriveArchiveKey(passphrase, salt, iterations);
  const parts = [concat(MAGIC, new Uint8Array([ARCHIVE_VERSION, KDF_PBKDF2_SHA512]), u32be(iterations), salt)];

  for (let off = 0; off < payload.length; off += CHUNK_BYTES) {
    const chunk = payload.subarray(off, Math.min(off + CHUNK_BYTES, payload.length));
    const nonce = nacl.randomBytes(24);
    const sealed = nacl.secretbox(chunk, nonce, key);
    parts.push(u32be(sealed.length + 24), nonce, sealed);
    onProgress?.(Math.min(off + CHUNK_BYTES, payload.length), payload.length);
  }
  // An empty archive is still a valid archive; without this, "I had no
  // messages" and "the file is broken" would look the same on import.
  if (payload.length === 0) {
    const nonce = nacl.randomBytes(24);
    const sealed = nacl.secretbox(new Uint8Array(0), nonce, key);
    parts.push(u32be(sealed.length + 24), nonce, sealed);
  }
  return concat(...parts);
}

/** Returns the payload bytes, or throws with a message a person can act on. */
export async function unpackArchive(passphrase, archive, { onProgress } = {}) {
  const h = parseArchiveHeader(archive);
  if (!h) throw new Error('Это не архив Voxiox или он повреждён');
  const key = await deriveArchiveKey(passphrase, h.salt, h.iterations);

  const chunks = [];
  let off = 0;
  while (off < h.body.length) {
    if (off + 4 > h.body.length) throw new Error('Архив обрывается на середине');
    const len = readU32be(h.body, off);
    off += 4;
    if (len < 24 + 16 || off + len > h.body.length) throw new Error('Архив обрывается на середине');
    const nonce = h.body.slice(off, off + 24);
    const sealed = h.body.slice(off + 24, off + len);
    off += len;
    const plain = nacl.secretbox.open(sealed, nonce, key);
    // The first chunk failing is almost always the passphrase; a later one is
    // almost always a damaged file. Saying which saves somebody retyping a
    // password that was right.
    if (!plain) {
      throw new Error(chunks.length === 0
        ? 'Неверная парольная фраза'
        : 'Архив повреждён: часть данных не расшифровывается');
    }
    chunks.push(plain);
    onProgress?.(off, h.body.length);
  }
  return concat(...chunks);
}

// ─── payload ────────────────────────────────────────────────────────────────

/**
 * Build what goes inside. Attachments are carried whole, base64 inside the
 * JSON — the alternative is an archive that restores a conversation with the
 * pictures missing and says nothing about it.
 */
export function buildPayload({ myUserId, conversations, attachments = [], exportedAt = null,
                               masterSecret = null }) {
  return new TextEncoder().encode(JSON.stringify({
    // Not bumped for the master key below. A version says "you cannot read
    // this"; an added optional field is readable by anything that ignores it,
    // and refusing an archive over a field you do not need is how a backup
    // stops restoring on the day it matters.
    v: 1,
    exportedAt: exportedAt || new Date().toISOString(),
    myUserId,
    conversations,
    attachments,
    // The account master key, which signs the device list. It is here because
    // keeping it on one device is the whole reason a hostile server cannot add
    // a device — and that same property means losing that device would leave
    // the account unable to admit another one, for ever. The archive is
    // already the road to a new phone and is already behind a passphrase, so
    // this is where it belongs.
    //
    // It also means the passphrase is now the account, not just the history.
    // That is a real change in what an archive is worth, and the export screen
    // has to say so.
    ...(masterSecret ? { masterSecret } : {}),
  }));
}

export function readPayload(bytes) {
  const parsed = JSON.parse(new TextDecoder().decode(bytes));
  if (parsed?.v !== 1) throw new Error('Неизвестная версия архива');
  return parsed;
}
